import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { boolean, object, string } from "yup";
import { LoyaltyLevel } from "../../../components/loyalty/utils";
import { Pageable } from "../../../services/pageable";
import { Roles } from "../../auth/models/internal";
import { AllowedBlockchains } from "../../chain/models/internal";
import { countries } from "../../common/models";
import { getFields, validationFields } from "../../common/models/fields";
import { StatusEnum, TwoFAProviders } from "./internal";

export namespace UsersDTO {
  export namespace Requests {
    export namespace UserDetails {
      export interface T {
        userReferenceId?: string;
        emailAddress: string;
        firstName: string;
        middleName: string;
        lastName: string;
        dateOfBirth: Date;
        nationality: typeof countries.T | "";
        wallets: Array<{
          address: string;
          chain: string;
        }>;
        streetAddress?: string;
        city?: string;
        zip?: string;
        state?: string;
        country?: typeof countries.T | "";
        phoneCountryCode?: string;
        receiveMarketingMaterial: boolean;
        phone: string;
      }

      export const useFields = () => {
        const { t } = useTranslation("fields");

        const emailSchema = validationFields.email.useSchema();
        const userReferenceIdSchema = validationFields.userReferenceId.useSchema();
        const firstNameSchema = validationFields.firstName.useSchema();
        const middleNameSchema = validationFields.middleName.useSchema();
        const lastNameSchema = validationFields.lastName.useSchema();
        const dateOfBirthSchema = validationFields.dateOfBirth.useSchema();
        const nationalitySchema = validationFields.nationality.useSchema();
        const walletsSchema = validationFields.wallets.useSchema();
        const citySchema = validationFields.city.useSchema();
        const zipSchema = validationFields.zip.useSchema();
        const stateSchema = validationFields.state.useSchema();
        const countrySchema = validationFields.country.useSchema();
        const phoneCountryCodeSchema = validationFields.phoneCountryCode.useSchema();
        const phoneSchema = validationFields.phone.useSchema();

        const userReferenceId = {
          name: validationFields.userReferenceId.useName(),
          schema: userReferenceIdSchema,
        };
        const emailAddress = {
          name: validationFields.email.useName(),
          schema: emailSchema,
        };
        const firstName = {
          name: validationFields.firstName.useName(),
          schema: firstNameSchema,
        };
        const middleName = {
          name: validationFields.middleName.useName(),
          schema: middleNameSchema,
        };
        const lastName = {
          name: validationFields.lastName.useName(),
          schema: lastNameSchema,
        };
        const dateOfBirth = {
          name: validationFields.dateOfBirth.useName(),
          schema: dateOfBirthSchema,
        };
        const nationality = {
          name: validationFields.nationality.useName(),
          schema: nationalitySchema,
        };
        const wallets = {
          name: validationFields.wallets.useName(),
          schema: walletsSchema,
        };
        const city = {
          name: validationFields.city.useName(),
          schema: citySchema,
        };
        const zip = {
          name: validationFields.zip.useName(),
          schema: zipSchema,
        };
        const state = {
          name: validationFields.state.useName(),
          schema: stateSchema,
        };
        const country = {
          name: validationFields.country.useName(),
          schema: countrySchema,
        };
        const phoneCountryCode = {
          name: validationFields.phoneCountryCode.useName(),
          schema: phoneCountryCodeSchema,
        };
        const phone = {
          name: validationFields.phone.useName(),
          schema: phoneSchema,
        };

        const streetAddressSchemaFn = validationFields.streetAddress.useSchema();
        const streetAddress = {
          name: validationFields.streetAddress.useName(),
          schema: useMemo(
            () =>
              streetAddressSchemaFn.required(
                t("required-field", {
                  ns: "errors",
                  field: t("street-address"),
                })
              ),
            [streetAddressSchemaFn, t]
          ),
        };

        return useMemo(
          () =>
            getFields({
              userReferenceId,
              emailAddress,
              firstName,
              middleName,
              lastName,
              dateOfBirth,
              nationality,
              wallets,
              streetAddress,
              city,
              zip,
              state,
              country,
              phoneCountryCode,
              phone,
            }),
          [
            userReferenceId.name,
            userReferenceId.schema,
            emailAddress.name,
            emailAddress.schema,
            firstName.name,
            firstName.schema,
            middleName.name,
            middleName.schema,
            lastName.name,
            lastName.schema,
            dateOfBirth.name,
            dateOfBirth.schema,
            nationality.name,
            nationality.schema,
            wallets.name,
            wallets.schema,
            streetAddress.name,
            streetAddress.schema,
            city.name,
            city.schema,
            zip.name,
            zip.schema,
            state.name,
            state.schema,
            country.name,
            country.schema,
            phoneCountryCode.name,
            phoneCountryCode.schema,
            phone.name,
            phone.schema,
          ]
        );
      };

      export const defaults: Partial<T> = {
        userReferenceId: "",
        emailAddress: "",
        firstName: "",
        middleName: "",
        lastName: "",
        nationality: "",
        wallets: [],
        streetAddress: "",
        city: "",
        zip: "",
        state: "",
        phoneCountryCode: "",
        phone: "",
        receiveMarketingMaterial: false,
      };
    }

    export namespace Merchant {
      export namespace Create {
        export interface T {
          name: string;
          nationality: typeof countries.T;
          apiUser: boolean;
          emailAddress: string;
          products: [];
        }

        export const useField = () => {
          const nationality = validationFields.nationality.useSchema();

          const email = validationFields.email.useSchema();

          return useMemo(
            () =>
              object().shape({
                name: string().required(),
                nationality: nationality,
                apiUser: boolean().required(),
                emailAddress: email,
              }),
            [nationality, email]
          );
        };
      }
    }
  }

  export namespace Responses {
    export namespace GetSingle {
      export interface T {
        addressStatus: typeof StatusEnum.T;
        amlStatus: typeof StatusEnum.T;
        believer: boolean;
        city?: string;
        companyDetails?: {
          enabled: boolean;
          id: string;
          name: string;
        };
        consentStatus: typeof StatusEnum.T;
        country?: typeof countries.T | null;
        createdDate: Date;
        currentProvider: typeof TwoFAProviders.T;
        dateOfBirth: Date;
        documentStatus: typeof StatusEnum.T;
        emailAddress: string;
        faceStatus: typeof StatusEnum.T;
        firstName?: string;
        kycStatus: typeof StatusEnum.T;
        lastName?: string;
        lastLoginTimestamp?: Date;
        level?: typeof LoyaltyLevel.T;
        locale: string;
        middleName?: string;
        nationality?: typeof countries.T | null;
        phone?: string | null;
        phoneCountryCode?: string | null;
        receiveMarketingMaterial: boolean;
        referrerToken: string;
        registeredProviders: Array<{
          registeredAt: Date;
          provider: typeof TwoFAProviders.T;
          state: "AUTHENTICATED" | "PENDING";
        }>;
        roleDetails: {
          delegate: boolean;
          roles: Array<typeof Roles.T>;
        };
        state?: string | null;
        streetAddress?: string;
        userAccountDetails: {
          accountNonExpired: boolean;
          accountNonLocked: boolean;
          credentialsNonExpired: boolean;
          emailVerified: boolean;
          enabled: boolean;
          loginTries: number;
        };
        userReferenceId: string;
        username: string;
        wallets: Array<{
          address: string;
          deleted: boolean;
          verified: boolean;
          nftMinted: boolean;
          chain: AllowedBlockchains;
          id: string;
        }>;
        zip?: string;
        payingSubscriber: boolean;

      }
    }

    export namespace GetAll {
      export interface T {
        content: GetSingle.T[];
        pageable: Pageable.Response.T;
      }

      export const defaults: T = {
        content: [],
        pageable: Pageable.Response.defaults,
      };
    }

    export namespace GetAllMerchants {
      export interface T {
        content: GetSingle.T[];
        pageable: Pageable.Response.T;
      }

      export const defaults: T = {
        content: [],
        pageable: Pageable.Response.defaults,
      };
    }

    export namespace Merchant {
      export namespace Create {
        export interface T {
          merchantId: string;
          credentialsId: string;
          generatedPassword: string;
        }
      }
    }
  }
}
